import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Package,
  Plus,
  Minus,
  Search,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Clock,
  User,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Edit,
  History,
  Pill,
  Warehouse,
  RefreshCw,
  Filter,
  FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getStockInReasonLabel = (reason, t) => t(`stockInReasons.${reason}`) || reason;
const getStockOutReasonLabel = (reason, t) => t(`stockOutReasons.${reason}`) || reason;

const SummaryDashboard = ({ t }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/inventory/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E63946]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('summary.totalMedications')}</p>
                <p className="text-2xl font-bold">{summary?.total_medications || 0}</p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Pill className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={summary?.low_stock_count > 0 ? 'border-yellow-300 bg-yellow-50' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('summary.lowStock')}</p>
                <p className="text-2xl font-bold text-yellow-600">{summary?.low_stock_count || 0}</p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={summary?.out_of_stock_count > 0 ? 'border-red-300 bg-red-50' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('summary.outOfStock')}</p>
                <p className="text-2xl font-bold text-red-600">{summary?.out_of_stock_count || 0}</p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
                <Package className="h-5 w-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('summary.expiringSoon')}</p>
                <p className="text-2xl font-bold text-orange-600">{summary?.expiring_soon_count || 0}</p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <Calendar className="h-5 w-5 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Transactions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('summary.recentMovements')}</CardTitle>
        </CardHeader>
        <CardContent>
          {summary?.recent_transactions?.length > 0 ? (
            <div className="space-y-3">
              {summary.recent_transactions.map((tx, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                      tx.transaction_type === 'stock_in' ? 'bg-green-100' :
                      tx.transaction_type === 'stock_out' ? 'bg-red-100' : 'bg-blue-100'
                    }`}>
                      {tx.transaction_type === 'stock_in' ? (
                        <ArrowUpRight className="h-4 w-4 text-green-600" />
                      ) : tx.transaction_type === 'stock_out' ? (
                        <ArrowDownRight className="h-4 w-4 text-red-600" />
                      ) : (
                        <RefreshCw className="h-4 w-4 text-blue-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{tx.medication_name}</p>
                      <p className="text-xs text-gray-500">
                        {tx.transaction_type === 'stock_in' ? getStockInReasonLabel(tx.reason, t) :
                         tx.transaction_type === 'stock_out' ? getStockOutReasonLabel(tx.reason, t) : 
                         t('stock.adjustment')}
                        {tx.patient_name && ` - ${tx.patient_name}`}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-medium ${
                      tx.transaction_type === 'stock_in' ? 'text-green-600' : 
                      tx.transaction_type === 'stock_out' ? 'text-red-600' : 'text-blue-600'
                    }`}>
                      {tx.transaction_type === 'stock_in' ? '+' : 
                       tx.transaction_type === 'stock_out' ? '-' : ''}
                      {tx.quantity} {tx.unit}
                    </p>
                    <p className="text-xs text-gray-400">
                      {new Date(tx.performed_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-4">{t('summary.noRecentMovements')}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const StockLevelsTab = ({ onStockAction, t }) => {
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [controlledOnly, setControlledOnly] = useState(false);

  useEffect(() => {
    fetchStock();
  }, [searchTerm, lowStockOnly, controlledOnly]);

  const fetchStock = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/inventory/stock`, {
        params: {
          search: searchTerm || undefined,
          low_stock_only: lowStockOnly || undefined,
          controlled_only: controlledOnly || undefined
        }
      });
      setStock(response.data);
    } catch (error) {
      console.error('Error fetching stock:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder={t('stock.searchPlaceholder')}
            className="pl-9"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={lowStockOnly}
            onChange={(e) => setLowStockOnly(e.target.checked)}
            className="rounded border-gray-300"
          />
          <span className="text-sm">{t('stock.lowStockOnly')}</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={controlledOnly}
            onChange={(e) => setControlledOnly(e.target.checked)}
            className="rounded border-gray-300"
          />
          <span className="text-sm">{t('stock.controlledOnly')}</span>
        </label>
        <Button variant="outline" size="sm" onClick={fetchStock}>
          <RefreshCw className="h-4 w-4 mr-1" />
          {t('stock.refresh')}
        </Button>
      </div>

      {/* Stock Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E63946]"></div>
        </div>
      ) : stock.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">{t('stock.table.medication')}</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">{t('stock.table.stock')}</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">{t('stock.table.min')}</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">{t('stock.table.patients')}</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">{t('stock.table.status')}</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">{t('stock.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {stock.map(item => (
                <tr key={item.medication_id} className="border-t hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-[#FDE8EA] flex items-center justify-center">
                        <Pill className="h-5 w-5 text-[#E63946]" />
                      </div>
                      <div>
                        <p className="font-medium">{item.medication_name}</p>
                        <p className="text-xs text-gray-500">
                          {item.generic_name} - {item.concentration}
                        </p>
                        <div className="flex gap-1 mt-1">
                          {item.is_controlled && (
                            <Badge variant="destructive" className="text-xs">{t('stock.badges.controlled')}</Badge>
                          )}
                          {item.requires_refrigeration && (
                            <Badge variant="outline" className="text-xs">{t('stock.badges.refrigerate')}</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className={`text-lg font-bold ${
                      item.is_out_of_stock ? 'text-red-600' :
                      item.is_low_stock ? 'text-yellow-600' : 'text-green-600'
                    }`}>
                      {item.total_stock}
                    </span>
                    <span className="text-sm text-gray-500 ml-1">{item.unit}</span>
                  </td>
                  <td className="py-3 px-4 text-center text-gray-500">
                    {item.min_stock_level}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {item.active_patients > 0 ? (
                      <Badge variant="outline">{item.active_patients} {t('stock.badges.active')}</Badge>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    {item.is_out_of_stock ? (
                      <Badge className="bg-red-100 text-red-800">{t('stock.status.outOfStock')}</Badge>
                    ) : item.is_low_stock ? (
                      <Badge className="bg-yellow-100 text-yellow-800">{t('stock.status.lowStock')}</Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-800">{t('stock.status.normal')}</Badge>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-green-600 border-green-300"
                        onClick={() => onStockAction('in', item)}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 border-red-300"
                        onClick={() => onStockAction('out', item)}
                        disabled={item.total_stock === 0}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onStockAction('history', item)}
                      >
                        <History className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>{t('stock.noMedications')}</p>
        </div>
      )}
    </div>
  );
};

const TransactionsTab = ({ t }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    fetchTransactions();
  }, [typeFilter]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/inventory/transactions`, {
        params: {
          transaction_type: typeFilter !== 'all' ? typeFilter : undefined,
          limit: 100
        }
      });
      setTransactions(response.data);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder={t('transactions.filterType')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('transactions.all')}</SelectItem>
            <SelectItem value="stock_in">{t('transactions.entries')}</SelectItem>
            <SelectItem value="stock_out">{t('transactions.exits')}</SelectItem>
            <SelectItem value="adjustment">{t('transactions.adjustments')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E63946]"></div>
        </div>
      ) : transactions.length > 0 ? (
        <div className="space-y-2">
          {transactions.map(tx => (
            <Card key={tx.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                      tx.transaction_type === 'stock_in' ? 'bg-green-100' :
                      tx.transaction_type === 'stock_out' ? 'bg-red-100' : 'bg-blue-100'
                    }`}>
                      {tx.transaction_type === 'stock_in' ? (
                        <ArrowUpRight className="h-5 w-5 text-green-600" />
                      ) : tx.transaction_type === 'stock_out' ? (
                        <ArrowDownRight className="h-5 w-5 text-red-600" />
                      ) : (
                        <RefreshCw className="h-5 w-5 text-blue-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">{tx.medication_name}</p>
                      <p className="text-sm text-gray-500">
                        {tx.transaction_type === 'stock_in' ? getStockInReasonLabel(tx.reason, t) :
                         tx.transaction_type === 'stock_out' ? getStockOutReasonLabel(tx.reason, t) :
                         tx.reason}
                      </p>
                      {tx.patient_name && (
                        <p className="text-xs text-blue-600">
                          <User className="h-3 w-3 inline mr-1" />
                          {tx.patient_name}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-lg font-bold ${
                      tx.transaction_type === 'stock_in' ? 'text-green-600' :
                      tx.transaction_type === 'stock_out' ? 'text-red-600' : 'text-blue-600'
                    }`}>
                      {tx.transaction_type === 'stock_in' ? '+' :
                       tx.transaction_type === 'stock_out' ? '-' : ''}
                      {tx.quantity} {tx.unit}
                    </p>
                    <p className="text-xs text-gray-500">
                      {t('transactions.stockChange')}: {tx.stock_before} → {tx.stock_after}
                    </p>
                    <p className="text-xs text-gray-400">
                      {new Date(tx.performed_at).toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-400">
                      {t('transactions.by')}: {tx.performed_by_name}
                    </p>
                  </div>
                </div>
                {tx.notes && (
                  <p className="mt-2 text-sm text-gray-500 bg-gray-50 p-2 rounded">
                    {tx.notes}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <History className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>{t('transactions.noTransactions')}</p>
        </div>
      )}
    </div>
  );
};

const StockInDialog = ({ medication, onClose, onSuccess, t }) => {
  const stockInReasonKeys = ['purchase', 'donation', 'return_from_patient', 'transfer_in', 'initial_stock', 'other'];
  const [formData, setFormData] = useState({
    medication_id: medication?.medication_id || '',
    quantity: 1,
    unit: medication?.unit || 'unidad',
    batch_number: '',
    lot_number: '',
    expiration_date: '',
    reason: 'purchase',
    reason_detail: '',
    supplier: '',
    reference_number: '',
    unit_cost: '',
    location: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (formData.quantity <= 0) {
      toast.error(t('messages.quantityMustBePositive'));
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/inventory/stock-in`, {
        ...formData,
        unit_cost: formData.unit_cost ? parseFloat(formData.unit_cost) : null,
        expiration_date: formData.expiration_date || null
      });
      toast.success(t('messages.stockInSuccess'));
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.errorStockIn'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="p-3 bg-green-50 rounded-lg">
        <p className="font-medium text-green-800">{t('form.stockIn.header')}</p>
        <p className="text-sm text-green-600">{medication?.medication_name}</p>
        <p className="text-xs text-green-500">{t('form.stockIn.currentStock')}: {medication?.total_stock} {medication?.unit}</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>{t('form.stockIn.quantity')}</Label>
          <Input
            type="number"
            min="1"
            value={formData.quantity}
            onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value) || 0})}
          />
        </div>
        <div>
          <Label>{t('form.stockIn.unit')}</Label>
          <Input
            value={formData.unit}
            onChange={(e) => setFormData({...formData, unit: e.target.value})}
          />
        </div>
      </div>

      <div>
        <Label>{t('form.stockIn.reason')}</Label>
        <Select value={formData.reason} onValueChange={(v) => setFormData({...formData, reason: v})}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {stockInReasonKeys.map((key) => (
              <SelectItem key={key} value={key}>{t(`stockInReasons.${key}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>{t('form.stockIn.batchNumber')}</Label>
          <Input
            value={formData.batch_number}
            onChange={(e) => setFormData({...formData, batch_number: e.target.value})}
            placeholder={t('form.stockIn.batchPlaceholder')}
          />
        </div>
        <div>
          <Label>{t('form.stockIn.expirationDate')}</Label>
          <Input
            type="date"
            value={formData.expiration_date}
            onChange={(e) => setFormData({...formData, expiration_date: e.target.value})}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>{t('form.stockIn.supplier')}</Label>
          <Input
            value={formData.supplier}
            onChange={(e) => setFormData({...formData, supplier: e.target.value})}
          />
        </div>
        <div>
          <Label>{t('form.stockIn.referenceNumber')}</Label>
          <Input
            value={formData.reference_number}
            onChange={(e) => setFormData({...formData, reference_number: e.target.value})}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>{t('form.stockIn.unitCost')}</Label>
          <Input
            type="number"
            step="0.01"
            value={formData.unit_cost}
            onChange={(e) => setFormData({...formData, unit_cost: e.target.value})}
            placeholder="0.00"
          />
        </div>
        <div>
          <Label>{t('form.stockIn.location')}</Label>
          <Input
            value={formData.location}
            onChange={(e) => setFormData({...formData, location: e.target.value})}
            placeholder={t('form.stockIn.locationPlaceholder')}
          />
        </div>
      </div>

      <div>
        <Label>{t('form.stockIn.notes')}</Label>
        <Textarea
          value={formData.notes}
          onChange={(e) => setFormData({...formData, notes: e.target.value})}
          rows={2}
        />
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>{t('form.stockIn.cancel')}</Button>
        <Button onClick={handleSubmit} disabled={saving} className="bg-green-600 hover:bg-green-700">
          {saving ? t('form.stockIn.saving') : t('form.stockIn.save')}
        </Button>
      </DialogFooter>
    </div>
  );
};

const StockOutDialog = ({ medication, onClose, onSuccess, t }) => {
  const stockOutReasonKeys = ['dispensed_to_patient', 'expired', 'damaged', 'lost', 'transfer_out', 'sample', 'other'];
  const [patients, setPatients] = useState([]);
  const [formData, setFormData] = useState({
    medication_id: medication?.medication_id || '',
    quantity: 1,
    unit: medication?.unit || 'unidad',
    batch_number: '',
    reason: 'dispensed_to_patient',
    reason_detail: '',
    patient_id: '',
    prescription_id: '',
    order_id: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPatients();
  }, []);

  const fetchPatients = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/patients?limit=100`);
      setPatients(response.data);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  const handleSubmit = async () => {
    if (formData.quantity <= 0) {
      toast.error(t('messages.quantityMustBePositive'));
      return;
    }
    if (formData.quantity > medication.total_stock) {
      toast.error(t('messages.insufficientStock'));
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/inventory/stock-out`, formData);
      toast.success(t('messages.stockOutSuccess'));
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.errorStockOut'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="p-3 bg-red-50 rounded-lg">
        <p className="font-medium text-red-800">{t('form.stockOut.header')}</p>
        <p className="text-sm text-red-600">{medication?.medication_name}</p>
        <p className="text-xs text-red-500">{t('form.stockOut.currentStock')}: {medication?.total_stock} {medication?.unit}</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>{t('form.stockOut.quantity')}</Label>
          <Input
            type="number"
            min="1"
            max={medication?.total_stock}
            value={formData.quantity}
            onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value) || 0})}
          />
        </div>
        <div>
          <Label>{t('form.stockOut.unit')}</Label>
          <Input value={formData.unit} disabled />
        </div>
      </div>

      <div>
        <Label>{t('form.stockOut.reason')}</Label>
        <Select value={formData.reason} onValueChange={(v) => setFormData({...formData, reason: v})}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {stockOutReasonKeys.map((key) => (
              <SelectItem key={key} value={key}>{t(`stockOutReasons.${key}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {formData.reason === 'dispensed_to_patient' && (
        <div>
          <Label>{t('form.stockOut.patient')}</Label>
          <Select value={formData.patient_id} onValueChange={(v) => setFormData({...formData, patient_id: v})}>
            <SelectTrigger>
              <SelectValue placeholder={t('form.stockOut.selectPatient')} />
            </SelectTrigger>
            <SelectContent>
              {patients.map(p => (
                <SelectItem key={p.id} value={p.id}>
                  {p.first_name} {p.last_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <Label>{t('form.stockOut.batchNumber')}</Label>
        <Input
          value={formData.batch_number}
          onChange={(e) => setFormData({...formData, batch_number: e.target.value})}
        />
      </div>

      <div>
        <Label>{t('form.stockOut.notes')}</Label>
        <Textarea
          value={formData.notes}
          onChange={(e) => setFormData({...formData, notes: e.target.value})}
          rows={2}
        />
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>{t('form.stockOut.cancel')}</Button>
        <Button onClick={handleSubmit} disabled={saving} className="bg-red-600 hover:bg-red-700">
          {saving ? t('form.stockOut.saving') : t('form.stockOut.save')}
        </Button>
      </DialogFooter>
    </div>
  );
};

export const InventoryPage = () => {
  const { t } = useTranslation('inventory');
  const [stockDialog, setStockDialog] = useState({ open: false, type: null, medication: null });

  const handleStockAction = (type, medication) => {
    if (type === 'history') {
      toast.info(t('messages.viewHistoryHint'));
      return;
    }
    setStockDialog({ open: true, type, medication });
  };

  const handleDialogClose = () => {
    setStockDialog({ open: false, type: null, medication: null });
  };

  const handleDialogSuccess = () => {
    setStockDialog({ open: false, type: null, medication: null });
  };

  return (
    <div className="space-y-6" data-testid="inventory-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('title')}</h1>
        <p className="text-gray-500 mt-1">{t('subtitle')}</p>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="summary" className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-6">
          <TabsTrigger value="summary" data-testid="summary-tab">
            <Warehouse className="h-4 w-4 mr-2" />
            {t('tabs.summary')}
          </TabsTrigger>
          <TabsTrigger value="stock" data-testid="stock-tab">
            <Package className="h-4 w-4 mr-2" />
            {t('tabs.stock')}
          </TabsTrigger>
          <TabsTrigger value="transactions" data-testid="transactions-tab">
            <History className="h-4 w-4 mr-2" />
            {t('tabs.transactions')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="summary">
          <SummaryDashboard t={t} />
        </TabsContent>

        <TabsContent value="stock">
          <Card>
            <CardHeader>
              <CardTitle>{t('stock.title')}</CardTitle>
              <CardDescription>{t('stock.subtitle')}</CardDescription>
            </CardHeader>
            <CardContent>
              <StockLevelsTab onStockAction={handleStockAction} t={t} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="transactions">
          <Card>
            <CardHeader>
              <CardTitle>{t('transactions.title')}</CardTitle>
              <CardDescription>{t('transactions.subtitle')}</CardDescription>
            </CardHeader>
            <CardContent>
              <TransactionsTab t={t} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Stock In Dialog */}
      <Dialog open={stockDialog.open && stockDialog.type === 'in'} onOpenChange={handleDialogClose}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('form.stockIn.title')}</DialogTitle>
          </DialogHeader>
          <StockInDialog
            medication={stockDialog.medication}
            onClose={handleDialogClose}
            onSuccess={handleDialogSuccess}
            t={t}
          />
        </DialogContent>
      </Dialog>

      {/* Stock Out Dialog */}
      <Dialog open={stockDialog.open && stockDialog.type === 'out'} onOpenChange={handleDialogClose}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('form.stockOut.title')}</DialogTitle>
          </DialogHeader>
          <StockOutDialog
            medication={stockDialog.medication}
            onClose={handleDialogClose}
            onSuccess={handleDialogSuccess}
            t={t}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryPage;
